/**
 * Copyright (C) 2023 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

/* eslint-disable no-undef */
import React from 'react';
import uniq from 'lodash/uniq';

import { connect, shallowEqual, useSelector } from 'react-redux';
import Terminal, { SteerableBeamPriority } from '../../shared/entities/terminal';
import { RootState, selectSpaceVehicleTerminals, selectTerminals } from '../../store';
import * as TerminalActions from '../../store/terminals/actions';
import * as UiActions from '../../store/ui/actions';
import { OverlaysContextsEnum, TailTagSize } from '../../store/maps/reducer';
import { isOnline } from '../../utils/ConnectionStatusUtils';
import { durationToNowInMin, MINUTES_IN_HOUR } from '../../utils/DateTimeUtils';
import { isNotNullOrUndefined } from '../../utils/NullUtils';
import { shouldRequestFlightPath } from '../../utils/PlatformTypeUtils';
import {
  getHaloNetSpaceNetworks,
  getTerminalDisplayName,
  terminalTypeSortPriorityFromTerminalTypeLabel,
} from '../../utils/TerminalUtilities';
import { GOOGLE_MAP_INSTANCE_KEY } from '../common/map/GoogleMapSingleton';
import TerminalIconMarker from './TerminalIconMarker';
import { SteerableBeamsRing } from '../../shared/entities/steerableBeam';
import { isNil } from 'lodash';
import { PortalsEnum } from '../../shared/entities/portal';

const MAX_TERMINAL_COUNT_KILL_SWITCH_IE = 100;
const MAX_TERMINAL_COUNT_KILL_SWITCH_OTHERS = 200;

export interface AddTailTagsToMapProps extends SpaceVehicleTagProp, StateProps, DispatchProps {
  map: google.maps.Map;
}

interface SpaceVehicleTagProp {
  isSpaceVehicleTag?: boolean;
}

/**
 * This class represents the container for any google map components that will be rendered on the map
 * in the fleet view. It is connected to the redux store and will pass any information to the dumb components
 * that will be drawn on the map.
 */

const AddTailTagsToMap = (props: AddTailTagsToMapProps) => {
  const {
    setSelectedDatapathUuid,
    setSelectedTerminalTabIndex,
    openDetailsPanel,
    syncRequestPollsForTerminalNoFlightPath,
    getFlightPathOnce,
    clearFlightPath,
    hiddenTerminals,
    isFullTailViewEnabled,
    selectedDatapathUuid,
    selectedTerminalTabIndex,
    tailTagsShown,
    terminalCountKillSwitchEnabled,
    terminalTagSize,
    steerableBeams,
    overlaysCurrentContext,
    map,
  } = props;

  const terminals = useSelector(
    overlaysCurrentContext === OverlaysContextsEnum.TRANSPORT_MONITORING
      ? selectSpaceVehicleTerminals
      : selectTerminals,
    shallowEqual,
  );

  const selectTerminal = (datapathUuid: string, terminalTabIndex: number, isDoubleClick: boolean = false) => {
    clearFlightPath();
    setSelectedDatapathUuid(datapathUuid);
    setSelectedTerminalTabIndex(terminalTabIndex);

    // if navigating to the FTV do not send unnecessary requests. FTV will handle the requests.
    if (!isDoubleClick) {
      const terminal = terminals[datapathUuid];

      if (shouldRequestFlightPath(terminal)) {
        getFlightPathOnce(datapathUuid);
      }
      syncRequestPollsForTerminalNoFlightPath(datapathUuid);
      openDetailsPanel();
    }
  };

  const isSelectedOrOnlineTerminalOnAircraft = (datapathUuid: string) => {
    const { selectedDatapathUuid } = props;
    const terminal = terminals[datapathUuid];
    const { datapathUuidsOnAircraft } = terminal;

    if (terminal && datapathUuidsOnAircraft && datapathUuidsOnAircraft.length > 0) {
      let isSelectedFound = false;

      datapathUuidsOnAircraft.forEach(datapathUuid => {
        if (datapathUuid) {
          const otherTerminal = terminals[datapathUuid];

          if (
            otherTerminal &&
            (otherTerminal.datapathUuid === selectedDatapathUuid || isOnline(otherTerminal.connectivityStatus))
          ) {
            isSelectedFound = true;
          }
        }
      });

      return isSelectedFound;
    }

    return false;
  };

  const getTerminals = (map: google.maps.Map) => {
    const altitudeUnit = 'ft';
    // Needed so aircraft with multiple terminals don't get rendered multiple times
    let terminalsAlreadyRendered = {};

    let terminalKeys = Object.keys(terminals).sort((a: string, b: string) =>
      terminalTypeSortPriorityFromTerminalTypeLabel(terminals[a].terminalTypeLabel) >
      terminalTypeSortPriorityFromTerminalTypeLabel(terminals[b].terminalTypeLabel)
        ? 1
        : -1,
    );

    // TODO: Take this kill switch out of codebase
    if (terminalCountKillSwitchEnabled) {
      const maxTerminalsAllowed = /Edge\/|Trident\/|MSIE /.test(window.navigator.userAgent)
        ? MAX_TERMINAL_COUNT_KILL_SWITCH_IE
        : MAX_TERMINAL_COUNT_KILL_SWITCH_OTHERS;

      if (terminalKeys.length > maxTerminalsAllowed) {
        terminalKeys = terminalKeys.reduce((accumulatedTerminalKeys: string[], datapathUuid: string) => {
          const terminal: Terminal = terminals[datapathUuid];

          if (terminal && (isOnline(terminal.connectivityStatus) || terminal.datapathUuid === selectedDatapathUuid)) {
            accumulatedTerminalKeys.push(datapathUuid);
          }

          return accumulatedTerminalKeys;
        }, []);
      }
    }

    terminalKeys = uniq(
      terminalKeys.filter((terminalKey: string) => {
        if (props.fleetViewFilters.length) {
          // @ts-ignore
          return props.fleetViewFilters.every(
            ({ key, value }) =>
              (terminals[terminalKey] && terminals[terminalKey][key] === value) ||
              (terminals[terminalKey].datapathUuidsOnAircraft || []).find(
                (datapathUuidOnAircraft: string) =>
                  terminals[datapathUuidOnAircraft] && terminals[datapathUuidOnAircraft][key] === value,
              ),
          );
        }
        return true;
      }),
    );

    return terminalKeys.reduce((accumulatedAircraftIcons: JSX.Element[], datapathUuid: string): JSX.Element[] => {
      const terminal: Terminal = terminals[datapathUuid];

      // Only show online terminals and offline terminals that have a message within the last hour unless selected
      if (
        terminal &&
        !terminalsAlreadyRendered[datapathUuid] &&
        !(hiddenTerminals && hiddenTerminals.includes(terminal.datapathUuid)) &&
        (isOnline(terminal.connectivityStatus) || // online terminal
          terminal.datapathUuid === selectedDatapathUuid || // selected terminal
          isSelectedOrOnlineTerminalOnAircraft(terminal.datapathUuid) || // selected or online terminal on aircraft. Ensures order of tail tags
          durationToNowInMin(terminal.sourceTime) <= MINUTES_IN_HOUR)
      ) {
        // only show terminals on the map if lat and long are reported
        if (isNotNullOrUndefined(terminal.latitudeDeg) && isNotNullOrUndefined(terminal.longitudeDeg)) {
          // received a message within the last hour
          // Add terminal and any other terminals on the aircraft to the already rendered map
          let otherTerminalsOnAircraft = null;
          let isTerminalSelected = selectedDatapathUuid === terminal.datapathUuid;
          let otherTerminalOnAircraftSelectedId = undefined;
          terminalsAlreadyRendered[datapathUuid] = true;
          let areAnyTerminalsOnAircraftUsingSteerableBeam = terminal.isUsingSteerableBeam;
          const steerableBeamData = steerableBeams.beams.find(
            (beam: SteerableBeamsRing) => beam.beamName === terminal.beamName,
          );
          const hcxShortestDistanceToBeamEdge =
            !isNil(steerableBeamData) && !isNil(steerableBeamData.terminalShortestDistanceToBeamEdgeMapping)
              ? steerableBeamData.terminalShortestDistanceToBeamEdgeMapping[terminal.datapathUuid]
              : null;

          if (terminal.datapathUuidsOnAircraft && terminal.datapathUuidsOnAircraft.length > 0) {
            otherTerminalsOnAircraft = [];
            terminal.datapathUuidsOnAircraft.forEach((otherDatapathUuid: string) => {
              if (otherDatapathUuid) {
                const otherTerminal = terminals[otherDatapathUuid];

                if (otherTerminal && props.fleetViewFilters.every(({ key, value }) => otherTerminal[key] === value)) {
                  areAnyTerminalsOnAircraftUsingSteerableBeam =
                    areAnyTerminalsOnAircraftUsingSteerableBeam || otherTerminal.isUsingSteerableBeam;
                  terminalsAlreadyRendered[otherDatapathUuid] = true;
                  otherTerminalsOnAircraft.push(otherTerminal);
                  if (otherDatapathUuid === selectedDatapathUuid) {
                    isTerminalSelected = true;
                    otherTerminalOnAircraftSelectedId = otherDatapathUuid;
                  }
                }
              }
            });
          }
          accumulatedAircraftIcons.push(
            <TerminalIconMarker
              key={terminal.datapathUuid}
              datapathUuid={terminal.datapathUuid}
              map={map as unknown as google.maps.Map}
              lat={terminal.latitudeDeg}
              lng={terminal.longitudeDeg}
              connectivityStatus={terminal.connectivityStatus}
              headingDeg={terminal.headingDeg}
              aircraftTailNumber={getTerminalDisplayName(terminal)}
              networkOrigin={terminal.networkOrigin}
              beamDisplayName={terminal.beamDisplayName}
              flDataRateBps={terminal.flDataRateBps}
              rlDataRateBps={terminal.rlDataRateBps}
              flightPhase={terminal.flightPhase}
              flSignalQuality={terminal.flSignalQuality}
              altitudeFeet={terminal.altitudeFeet}
              altitudeUnit={altitudeUnit}
              platformType={terminal.platformType}
              terminalTypeLabel={terminal.terminalTypeLabel}
              networkGroup={terminal.networkGroup}
              installationType={terminal.installationType}
              selected={isTerminalSelected}
              selectTerminal={selectTerminal}
              selectedTerminalTabIndex={selectedTerminalTabIndex}
              otherTerminalsOnAircraft={otherTerminalsOnAircraft}
              otherTerminalOnAircraftSelectedId={otherTerminalOnAircraftSelectedId}
              isTailTagTransparent={!!selectedDatapathUuid}
              sourceTime={terminal.sourceTime}
              isTailTagShown={tailTagsShown}
              isFullTailViewEnabled={isFullTailViewEnabled}
              isPrimaryTerminal={terminal.steerableBeamPriority === SteerableBeamPriority.PRIMARY}
              isSecondaryTerminal={terminal.steerableBeamPriority === SteerableBeamPriority.SECONDARY}
              useGreatestSteerableBeamPriorityIconOfAllTerminalsOnAircraft
              hcxShortestDistanceToBeamEdge={hcxShortestDistanceToBeamEdge}
              areAnyTerminalsOnAircraftUsingSteerableBeam={areAnyTerminalsOnAircraftUsingSteerableBeam}
              disableAntennaTags={terminalTagSize === TailTagSize.SMALL}
              lwSiteId={terminal.lwSiteId}
              lwTxPowerDb={terminal.lwTxPowerDb}
              spaceNetworks={getHaloNetSpaceNetworks(terminal, otherTerminalsOnAircraft?.[0])}
            />,
          );
        }
      }

      return accumulatedAircraftIcons;
    }, []);
  };

  return <React.Fragment>{getTerminals(map)}</React.Fragment>;
};

const mapStateToProps = (state: RootState) => ({
  selectedDatapathUuid:
    state.ui.overlaysContextUiState[state.ui.overlaysCurrentContext].selectedDatapathUuidInPlatformList,
  selectedTerminalTabIndex: state.ui.overlaysContextUiState[state.ui.overlaysCurrentContext].selectedTerminalTabIndex,
  selectedTerminalFlightPath:
    state.terminals.selectedTerminalFlightPath &&
    state.ui.overlaysContextUiState[state.ui.overlaysCurrentContext].selectedDatapathUuidInPlatformList
      ? state.terminals.selectedTerminalFlightPath[
          state.ui.overlaysContextUiState[state.ui.overlaysCurrentContext].selectedDatapathUuidInPlatformList as string
        ]
      : undefined,
  terminalTagSize:
    state.maps.mapContext[GOOGLE_MAP_INSTANCE_KEY]?.overlaysUiState[state.maps.overlaysCurrentContext]?.terminalTagSize,
  overlaysCurrentContext: state.maps.overlaysCurrentContext,
  tailTagsShown:
    state.maps.mapContext[GOOGLE_MAP_INSTANCE_KEY]?.overlaysUiState[state.maps.overlaysCurrentContext]?.tailTagsShown,
  isFullTailViewEnabled: state.config.appConfig.isFullTailViewEnabled,
  terminalCountKillSwitchEnabled: state.config.appConfig.terminalCountKillSwitchEnabled,
  fleetViewFilters: state.ui.overlaysContextUiState[state.ui.overlaysCurrentContext].terminalFilters,
  hiddenTerminals: state.ui.overlaysContextUiState[state.ui.overlaysCurrentContext].hiddenTerminalsList,
  steerableBeams: state.steerableBeams,
});
type StateProps = ReturnType<typeof mapStateToProps>;

export const mapDispatchToProps = (dispatch: any, ownProps: SpaceVehicleTagProp) => {
  return {
    setSelectedDatapathUuid: (datapathUuid: string) =>
      dispatch(UiActions.setSelectedDatapathUuidInPlatformList(datapathUuid)),
    setSelectedTerminalTabIndex: (index: number) => dispatch(UiActions.setSelectedTerminalTabIndex(index)),
    getFlightPathOnce: (datapathUuid: string) => {
      if (ownProps?.isSpaceVehicleTag) {
        return dispatch(TerminalActions.getFlightPathOnce(datapathUuid, PortalsEnum.ISAN_PORTAL));
      }
      return dispatch(TerminalActions.getFlightPathOnce(datapathUuid));
    },
    syncRequestPollsForTerminalNoFlightPath: (datapathUuid: string) => {
      if (ownProps?.isSpaceVehicleTag) {
        return dispatch(
          TerminalActions.syncRequestPollsForTerminal(datapathUuid, true, false, true, PortalsEnum.ISAN_PORTAL),
        );
      }
      return dispatch(TerminalActions.syncRequestPollsForTerminal(datapathUuid, true, false));
    },
    openDetailsPanel: () => dispatch(UiActions.openDetailsPanel()),
    clearFlightPath: () => {
      if (ownProps?.isSpaceVehicleTag) {
        return dispatch(TerminalActions.clearFlightPath(PortalsEnum.ISAN_PORTAL));
      }
      return dispatch(TerminalActions.clearFlightPath());
    },
  };
};
type DispatchProps = ReturnType<typeof mapDispatchToProps>;

export default connect(mapStateToProps, mapDispatchToProps)(AddTailTagsToMap);
